import { useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useUnselectAll
} from 'react-admin';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { API_BASE_URL, authFetchJson } from '../apiClient.ts';
import { ContactAutocomplete } from '../components/ContactAutocomplete.tsx';
import { RecordHeader, RecordPage, RecordSection } from '../components/record/RecordPage.tsx';
import { useMailAccounts } from '../settings/useMailAccounts.ts';
import {
  describeContactSync,
  describeScanOutcome,
  fetchMailAccounts,
  formatDateTime,
  loadScanWindow,
  providerLabels,
  saveScanWindow,
  SCAN_WINDOWS,
  scanMailAccount,
  type ScanWindow,
  syncMailContacts,
  type MailAccount
} from '../settings/mailIntegrations.ts';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { getErrorMessage } from '../utils/helperUtils.ts';

type Confidence = 'high' | 'medium' | 'low';
type ContactRef = { id: number; firstName: string; lastName: string } | null;
type SuggestedGroup = { groupId: number | null; name: string; confidence: Confidence };

export type ContactSuggestion = {
  id: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  source: 'INBOX_SCAN' | 'ADDRESS_BOOK';
  confidence: Confidence | null;
  reasoning: string | null;
  mailAccountId: number | null;
  sourceMessageId: string;
  sourceSubject: string | null;
  sourceFrom: string | null;
  sourceReceivedAt: string | null;
  sourceSnippet: string | null;
  sourceWebLink: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  companyName: string | null;
  linkedInUrl: string | null;
  websiteUrl: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  notes: string | null;
  referredByName: string | null;
  referredByEmail: string | null;
  referredByContactId: number | null;
  referredByContact: ContactRef;
  suggestedGroups: SuggestedGroup[] | null;
  createdContactId: number | null;
  createdContact: ContactRef;
  createdAt: string;
};

const RESOURCE = 'contact-suggestions';

const confidenceColor = (c: Confidence | null) =>
  c === 'high' ? 'success' : c === 'medium' ? 'warning' : 'default';

const fullName = (r: { firstName: string | null; lastName: string | null }) =>
  `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || 'Unknown name';

const stopRowClick = (event: MouseEvent) => event.stopPropagation();

const post = (path: string, body?: unknown) =>
  authFetchJson(`${API_BASE_URL}/${RESOURCE}/${path}`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body ?? {})
  });

const patch = (path: string, body: unknown) =>
  authFetchJson(`${API_BASE_URL}/${RESOURCE}/${path}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });

type ApproveResponse = { id: number; contactId: number; contactWasExisting: boolean };

type SuggestionTab = ContactSuggestion['source'];

// Missing contacts leads: it is the tab people use most, and the referral
// queue is still being tuned.
const suggestionTabs: { value: SuggestionTab; label: string }[] = [
  { value: 'ADDRESS_BOOK', label: 'Missing contacts' },
  { value: 'INBOX_SCAN', label: 'Referrals' }
];

// ---------- filters ----------

const confidenceFilters: { key: string; label: string; value?: Confidence[] }[] = [
  { key: 'all', label: 'All' },
  { key: 'high', label: 'High confidence', value: ['high'] },
  { key: 'review', label: 'Needs a look', value: ['medium', 'low'] }
];

// "All" is the server default: everything at or above the configured minimum
// confidence. "Needs a look" is the only way to see rows below it.
const ConfidenceChips = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = JSON.stringify(filterValues?.confidence ?? null);

  // Drop the key entirely for "All" so react-admin sees no active filter and
  // can render the empty state instead of a blank grid.
  const select = (value?: Confidence[]) => {
    const next = { ...filterValues };
    delete next.confidence;
    setFilters(value ? { ...next, confidence: value } : next, displayedFilters);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ px: 2, pt: 2, pb: 1, flexWrap: 'wrap', gap: 0.5 }}>
      {confidenceFilters.map((option) => {
        const selected = JSON.stringify(option.value ?? null) === current;
        return (
          <Chip
            key={option.key}
            label={option.label}
            clickable
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'filled' : 'outlined'}
            onClick={() => select(option.value)}
          />
        );
      })}
    </Stack>
  );
};

// ---------- row detail ----------

const Detail = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ py: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Box>
);

const Provenance = () => {
  const record = useRecordContext<ContactSuggestion>();
  if (!record) return null;

  const address = [
    record.street,
    [record.city, record.state].filter(Boolean).join(', '),
    record.zip
  ]
    .filter(Boolean)
    .join(' ');

  const fromAddressBook = record.source === 'ADDRESS_BOOK';

  return (
    <Box
      sx={{
        p: 1.5,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {fromAddressBook ? 'Address book entry' : 'Source email'}
        </Typography>
        {fromAddressBook ? (
          <>
            <Detail label="Found in">{record.sourceSubject ?? 'Connected address book'}</Detail>
            <Detail label="Mailbox">{record.sourceFrom ?? '(unknown)'}</Detail>
            {record.email ? <Detail label="Email">{record.email}</Detail> : null}
          </>
        ) : (
          <>
            <Detail label="Subject">{record.sourceSubject ?? '(no subject)'}</Detail>
            <Detail label="From">{record.sourceFrom ?? '(unknown)'}</Detail>
            <Detail label="Received">
              {formatDateTime(record.sourceReceivedAt) || '(unknown)'}
            </Detail>
            <Detail label="Preview">{record.sourceSnippet ?? '(none)'}</Detail>
            {record.sourceWebLink ? (
              <Detail label="Message">
                <MuiLink href={record.sourceWebLink} target="_blank" rel="noopener noreferrer">
                  Open the email
                </MuiLink>
              </Detail>
            ) : null}
          </>
        )}
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Extracted details
        </Typography>
        <Detail label="Why it was suggested">{record.reasoning ?? '(no reasoning given)'}</Detail>
        {record.phone ? <Detail label="Phone">{record.phone}</Detail> : null}
        {address ? <Detail label="Address">{address}</Detail> : null}
        {record.linkedInUrl ? <Detail label="LinkedIn">{record.linkedInUrl}</Detail> : null}
        {record.websiteUrl ? <Detail label="Website">{record.websiteUrl}</Detail> : null}
        {record.notes ? <Detail label="Notes">{record.notes}</Detail> : null}
        {record.suggestedGroups?.length ? (
          <Detail label="Suggested groups">
            {record.suggestedGroups.map((g) => `${g.name} (${g.confidence})`).join(', ')}
          </Detail>
        ) : null}
      </Box>
    </Box>
  );
};

// ---------- edit & approve ----------

type EditableFields = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
  companyName: string;
};

const EditApproveDialog = ({
  record,
  open,
  busy,
  onClose,
  onSubmit
}: {
  record: ContactSuggestion;
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (body: Partial<EditableFields> & { referredByContactId?: number | null }) => void;
}) => {
  const [form, setForm] = useState<EditableFields>({
    firstName: record.firstName ?? '',
    lastName: record.lastName ?? '',
    email: record.email ?? '',
    phone: record.phone ?? '',
    title: record.title ?? '',
    companyName: record.companyName ?? ''
  });
  const [referredById, setReferredById] = useState<number | null>(record.referredByContactId);

  const update = (key: keyof EditableFields) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = () =>
    onSubmit({
      ...form,
      // Only send the referrer when it changed, so an untouched empty value
      // still lets the API fall back to its own lookup.
      ...(referredById !== record.referredByContactId ? { referredByContactId: referredById } : {})
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" onClick={stopRowClick}>
      <DialogTitle>Edit and approve</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="First name"
              value={form.firstName}
              onChange={update('firstName')}
              fullWidth
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Last name"
              value={form.lastName}
              onChange={update('lastName')}
              fullWidth
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Email" value={form.email} onChange={update('email')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Phone" value={form.phone} onChange={update('phone')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Title" value={form.title} onChange={update('title')} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Company"
              value={form.companyName}
              onChange={update('companyName')}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <ContactAutocomplete
              value={referredById}
              onChange={setReferredById}
              label="Referred by"
            />
            {record.referredByName && !referredById ? (
              <Typography variant="caption" color="text.secondary">
                The email names {record.referredByName}
                {record.referredByEmail ? ` <${record.referredByEmail}>` : ''}. Pick the matching
                contact, or leave this blank and Forge will look them up by name and email when you
                approve.
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !form.firstName.trim() || !form.lastName.trim()}
          onClick={submit}
        >
          Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- row actions ----------

// A referral whose referrer the scan could not match to a contact. The pick
// is saved on the row at once, so it holds through bulk approve and refresh.
const ReferrerPicker = ({ record }: { record: ContactSuggestion }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  const choose = async (id: number | null) => {
    if (id === record.referredByContactId) return;
    setBusy(true);
    try {
      await patch(`${record.id}/referrer`, { referredByContactId: id });
      refresh();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box onClick={stopRowClick} sx={{ minWidth: 220, opacity: busy ? 0.6 : 1 }}>
      {record.referredByName || record.referredByEmail ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Email names {record.referredByName ?? record.referredByEmail}, not in Forge yet
        </Typography>
      ) : null}
      <ContactAutocomplete
        value={record.referredByContactId}
        onChange={(id) => void choose(id)}
        label="Referred by"
      />
    </Box>
  );
};

const RowActions = () => {
  const record = useRecordContext<ContactSuggestion>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  if (!record) return null;

  const approve = async (body?: unknown) => {
    setBusy(true);
    try {
      const { json } = await post(`${record.id}/approve`, body);
      const result = json as ApproveResponse;
      notify(
        result.contactWasExisting
          ? `Linked to existing contact ${fullName(record)}`
          : `Created contact ${fullName(record)}`,
        { type: 'success' }
      );
      setEditing(false);
      refresh();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await post(`${record.id}/reject`);
      notify('Suggestion rejected', { type: 'info' });
      refresh();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // A contact needs both names; an email-only row goes through the dialog.
  const nameless = !record.firstName?.trim() || !record.lastName?.trim();

  return (
    <Stack spacing={0.75} onClick={stopRowClick} sx={{ width: 160 }}>
      {nameless ? null : (
        <Button
          size="small"
          variant="contained"
          fullWidth
          disabled={busy}
          onClick={() => approve()}
        >
          Approve
        </Button>
      )}
      <Button
        size="small"
        variant={nameless ? 'contained' : 'outlined'}
        fullWidth
        disabled={busy}
        onClick={() => setEditing(true)}
      >
        {nameless ? 'Add name & approve' : 'Edit & approve'}
      </Button>
      <Button size="small" color="error" fullWidth disabled={busy} onClick={reject}>
        Reject
      </Button>
      {editing ? (
        <EditApproveDialog
          record={record}
          open={editing}
          busy={busy}
          onClose={() => setEditing(false)}
          onSubmit={approve}
        />
      ) : null}
    </Stack>
  );
};

const BulkApproveButton = () => {
  const { selectedIds } = useListContext();
  const unselectAll = useUnselectAll(RESOURCE);
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { json } = await post('bulk-approve', { ids: selectedIds });
      const result = json as {
        approved: number;
        failed: number;
        results: Array<{ id: number; ok: boolean; error?: string }>;
      };
      const failures = result.results.filter((r) => !r.ok);
      notify(
        failures.length
          ? `Approved ${result.approved}, ${result.failed} failed: ${failures
              .map((f) => `#${f.id} ${f.error ?? ''}`.trim())
              .join('; ')}`
          : `Approved ${result.approved} suggestion${result.approved === 1 ? '' : 's'}`,
        { type: failures.length ? 'warning' : 'success', autoHideDuration: 8000 }
      );
      unselectAll();
      refresh();
    } catch (err) {
      notify(getErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size="small" variant="contained" disabled={busy || !selectedIds.length} onClick={run}>
      Approve selected ({selectedIds.length})
    </Button>
  );
};

// ---------- header, empty states ----------

// Per-mailbox status for the active tab. Buttons live in the page header.
const MailboxStatus = ({
  tab,
  account,
  accounts,
  loading
}: {
  tab: SuggestionTab;
  account: MailAccount | null;
  accounts: MailAccount[];
  loading: boolean;
}) => {
  const referrals = tab === 'INBOX_SCAN';
  return (
    <RecordSection
      title="Connected mailboxes"
      description={
        referrals
          ? 'Scan reads recent inbox mail for people being introduced or referred to you.'
          : 'Sync reads your address book and the people you exchange email with.'
      }
      action={
        account?.needsReconnect ? (
          <Button component={RouterLink} to="/settings" size="small" variant="outlined">
            Reconnect in Settings
          </Button>
        ) : null
      }
      sx={{ mb: 2 }}
    >
      {loading && !account ? (
        <Typography variant="body2" color="text.secondary">
          Loading mailbox
        </Typography>
      ) : account ? (
        <Stack spacing={0.75}>
          {accounts.map((a) => (
            <Box key={a.id}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {providerLabels[a.provider]}: {a.emailAddress}
                {a.needsReconnect ? ' (reconnect needed)' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {referrals
                  ? a.scanRunning
                    ? `Scanning now: ${a.lastScanScanned ?? 0} messages read, ${a.lastScanSuggested ?? 0} queued so far`
                    : a.lastScanAt
                      ? a.lastScanStatus === 'error'
                        ? `Last scan ${formatDateTime(a.lastScanAt)} failed after ${a.lastScanScanned ?? 0} messages: ${a.lastScanError ?? 'unknown error'}`
                        : `Last scan ${formatDateTime(a.lastScanAt)}${a.lastScanLookbackDays ? ` (${a.lastScanLookbackDays}-day window)` : ''}: ${a.lastScanSummary ?? `${a.lastScanScanned ?? 0} messages, ${a.lastScanSuggested ?? 0} suggested`}`
                      : 'Not scanned yet'
                  : describeContactSync(a, 'Reconnect in Settings to allow contact sync')}
                {!referrals && a.canSyncContacts && !a.canSyncPeople
                  ? ' · Reconnect in Settings to include the people you email'
                  : ''}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No mailbox connected.{' '}
          <MuiLink component={RouterLink} to="/settings">
            Connect a mailbox in Settings
          </MuiLink>{' '}
          to start.
        </Typography>
      )}
    </RecordSection>
  );
};

// Header actions: the scan window and Scan Inboxes on Referrals, Sync
// contacts on Missing contacts.
const ScanActions = ({
  tab,
  account,
  accounts,
  scanning,
  syncing,
  scanWindow,
  onScanWindowChange,
  onScan,
  onSync
}: {
  tab: SuggestionTab;
  account: MailAccount | null;
  accounts: MailAccount[];
  scanning: boolean;
  syncing: boolean;
  scanWindow: ScanWindow;
  onScanWindowChange: (days: ScanWindow) => void;
  onScan: () => void;
  onSync: () => void;
}) => {
  const syncable = accounts.filter((a) => a.canSyncContacts && !a.needsReconnect);
  const peopleMissing = syncable.some((a) => !a.canSyncPeople);

  if (tab === 'INBOX_SCAN') {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Select
          size="small"
          value={scanWindow}
          onChange={(e) => onScanWindowChange(Number(e.target.value) as ScanWindow)}
          disabled={scanning || syncing}
          sx={{ minWidth: 130 }}
        >
          {SCAN_WINDOWS.map((days) => (
            <MenuItem key={days} value={days}>
              Last {days} days
            </MenuItem>
          ))}
        </Select>
        <Tooltip
          title={
            !account
              ? 'Connect a mailbox first'
              : account.needsReconnect
                ? 'Reconnect the mailbox first'
                : `Reads the last ${scanWindow} days of inbox mail and queues anyone being referred to you`
          }
        >
          <span>
            <Button
              variant="contained"
              startIcon={
                scanning ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ManageSearchOutlinedIcon />
                )
              }
              disabled={!account || account.needsReconnect || scanning || syncing}
              onClick={onScan}
            >
              {scanning ? 'Scanning' : accounts.length > 1 ? 'Scan Inboxes' : 'Scan Inbox'}
            </Button>
          </span>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Tooltip
      title={
        !account
          ? 'Connect a mailbox first'
          : !syncable.length
            ? 'Reconnect the mailbox to allow address book access'
            : peopleMissing
              ? 'Reads saved contacts; reconnect to include the people you email'
              : 'Reads saved contacts and the people you email, and queues anyone not yet in Forge'
      }
    >
      <span>
        <Button
          variant="contained"
          startIcon={
            syncing ? <CircularProgress size={16} color="inherit" /> : <ContactsOutlinedIcon />
          }
          disabled={!syncable.length || syncing || scanning}
          onClick={onSync}
        >
          {syncing ? 'Syncing' : 'Sync contacts'}
        </Button>
      </span>
    </Tooltip>
  );
};

// Never-scanned and nothing-found mean different things. A single blank state
// would hide a broken integration.
const EmptyState = ({
  tab,
  account,
  accounts,
  loading,
  scanning,
  syncing,
  onScan,
  onSync
}: {
  tab: SuggestionTab;
  account: MailAccount | null;
  accounts: MailAccount[];
  loading: boolean;
  scanning: boolean;
  syncing: boolean;
  onScan: () => void;
  onSync: () => void;
}) => {
  if (loading && !account) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!account) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">No mailbox connected</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Connect Outlook and Forge will read your inbox for the people being referred to you.
        </Typography>
        <Button component={RouterLink} to="/settings" variant="contained">
          Open Settings
        </Button>
      </Box>
    );
  }

  if (tab === 'ADDRESS_BOOK') {
    const syncable = accounts.filter((a) => a.canSyncContacts && !a.needsReconnect);
    const synced = accounts.find((a) => a.lastContactSyncAt);
    const inFlight = accounts.filter((a) => a.contactSyncRunning);
    if (syncing || inFlight.length) {
      const queued = inFlight.reduce((sum, a) => sum + (a.lastContactSyncFound ?? 0), 0);
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={24} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Syncing your contacts
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {queued} people queued so far. A big mailbox takes a few minutes; you can leave and come
            back.
          </Typography>
        </Box>
      );
    }
    if (!syncable.length) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Address book access not granted yet</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {account.emailAddress} was connected before Forge asked for address book access.
            Disconnect and connect it again in Settings, then sync.
          </Typography>
          <Button component={RouterLink} to="/settings" variant="contained">
            Open Settings
          </Button>
        </Box>
      );
    }
    if (!synced) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6">Not synced yet</Typography>
          <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Sync reads the address book of every connected mailbox and lists anyone who is not in
            Forge.
          </Typography>
          <Button variant="contained" disabled={syncing} onClick={onSync}>
            {syncing ? 'Syncing' : 'Sync contacts'}
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Nothing waiting for review</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          The last sync on {formatDateTime(synced.lastContactSyncAt)} found nobody new. Sync again
          after people are added to your address book.
        </Typography>
      </Box>
    );
  }

  const running = accounts.filter((a) => a.scanRunning);
  if (scanning || running.length) {
    const read = running.reduce((sum, a) => sum + (a.lastScanScanned ?? 0), 0);
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {running.length > 1 ? 'Scanning your inboxes' : 'Scanning your inbox'}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {read} messages read so far. This can take a few minutes for a busy mailbox; you can leave
          and come back.
        </Typography>
      </Box>
    );
  }

  if (!account.lastScanAt) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Not scanned yet</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          {account.emailAddress} is connected but has not been scanned. Run the first scan to fill
          this queue.
        </Typography>
        <Button variant="contained" disabled={scanning || account.needsReconnect} onClick={onScan}>
          {scanning ? 'Scanning' : 'Scan Inbox'}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6">Nothing waiting for review</Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        The last scan on {formatDateTime(account.lastScanAt)} found nothing new. Scan again after
        new mail arrives, or use the Needs a look filter to see lower-confidence rows.
      </Typography>
    </Box>
  );
};

const NoMatches = () => (
  <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
    No suggestions match this filter.
  </Typography>
);

// ---------- page ----------

const ContactSuggestionList = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { accounts, loading, reload } = useMailAccounts();
  const [scanning, setScanning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState<SuggestionTab>('ADDRESS_BOOK');
  const [scanWindow, setScanWindow] = useState<ScanWindow>(() => loadScanWindow());
  const pickScanWindow = (days: ScanWindow) => {
    setScanWindow(days);
    saveScanWindow(days);
  };

  // One mailbox per provider. Scan every one that can be scanned; the bar and
  // empty states lead with whichever is healthiest.
  const scannable = accounts.filter((a) => !a.needsReconnect);
  const account = scannable[0] ?? accounts[0] ?? null;

  // Scans and syncs run on the server; this only starts them and watches the
  // mailbox rows until every started job has reported an outcome.
  const polling = useRef(false);
  const watch = async (kind: 'scan' | 'sync', ids: number[]) => {
    if (polling.current) return;
    polling.current = true;
    const setBusy = kind === 'scan' ? setScanning : setSyncing;
    const running = (a: MailAccount) => (kind === 'scan' ? a.scanRunning : a.contactSyncRunning);
    setBusy(true);
    const deadline = Date.now() + 45 * 60 * 1000;
    try {
      let latest: MailAccount[] = accounts;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        try {
          latest = await fetchMailAccounts();
        } catch {
          continue;
        }
        await reload();
        if (!latest.some((a) => ids.includes(a.id) && running(a))) break;
      }
      for (const a of latest.filter((x) => ids.includes(x.id))) {
        const ok = kind === 'scan' ? a.lastScanStatus === 'ok' : a.lastContactSyncStatus === 'ok';
        notify(
          kind === 'scan'
            ? describeScanOutcome(a)
            : `${a.emailAddress}: ${describeContactSync(a, 'reconnect in Settings to allow contact sync')}`,
          { type: ok ? 'success' : 'warning', autoHideDuration: 10000 }
        );
      }
    } finally {
      polling.current = false;
      setBusy(false);
      refresh();
    }
  };

  const handleSync = async () => {
    const targets = scannable.filter((a) => a.canSyncContacts);
    if (!targets.length) return;
    setSyncing(true);
    const started: number[] = [];
    for (const target of targets) {
      try {
        const result = await syncMailContacts(target.id);
        if (result.status === 'running') {
          started.push(result.accountId);
        } else {
          notify(`${target.emailAddress}: ${result.message ?? 'Reconnect the mailbox first.'}`, {
            type: 'warning',
            autoHideDuration: 10000
          });
        }
      } catch (err) {
        notify(`${target.emailAddress}: ${getErrorMessage(err)}`, {
          type: 'error',
          autoHideDuration: 10000
        });
      }
    }
    if (started.length) {
      await watch('sync', started);
    } else {
      setSyncing(false);
      await reload();
    }
  };

  const handleScan = async () => {
    if (!scannable.length) return;
    setScanning(true);
    const started: number[] = [];
    for (const target of scannable) {
      try {
        const result = await scanMailAccount(target.id, scanWindow);
        started.push(result.accountId);
      } catch (err) {
        notify(`${target.emailAddress}: ${getErrorMessage(err)}`, {
          type: 'error',
          autoHideDuration: 10000
        });
      }
    }
    if (started.length) {
      await watch('scan', started);
    } else {
      setScanning(false);
    }
  };

  // A job started earlier (or from another tab) is still running: pick it
  // up so a refresh shows progress instead of a stale bar.
  const runningScanIds = accounts.filter((a) => a.scanRunning).map((a) => a.id);
  const runningSyncIds = accounts.filter((a) => a.contactSyncRunning).map((a) => a.id);
  const runningKey = `${runningScanIds.join(',')}|${runningSyncIds.join(',')}`;
  useEffect(() => {
    if (polling.current) return;
    if (runningScanIds.length) void watch('scan', runningScanIds);
    else if (runningSyncIds.length) void watch('sync', runningSyncIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningKey]);

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2, mb: 1 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Email Suggestions</Typography>
      </Breadcrumbs>

      <RecordPage>
        <RecordHeader
          avatar={
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
              <MailOutlineIcon />
            </Avatar>
          }
          title="Email Suggestions"
          subtitle="People from your connected mailboxes who are not in Forge yet. Approve to create the contact, or reject to bury the suggestion."
          actions={
            <ScanActions
              tab={tab}
              account={account}
              accounts={accounts}
              scanning={scanning}
              syncing={syncing}
              scanWindow={scanWindow}
              onScanWindowChange={pickScanWindow}
              onScan={handleScan}
              onSync={handleSync}
            />
          }
        />

        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 1.5 }}>
          <Tabs
            value={tab}
            onChange={(_, next: SuggestionTab) => setTab(next)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {suggestionTabs.map((option) => (
              <Tab key={option.value} value={option.value} label={option.label} />
            ))}
          </Tabs>
        </Box>

        <MailboxStatus tab={tab} account={account} accounts={accounts} loading={loading} />

        <List
          filter={{ status: 'PENDING', source: tab }}
          sort={{ field: 'createdAt', order: 'DESC' }}
          exporter={false}
          actions={false}
          title="Email Suggestions"
          empty={
            <EmptyState
              tab={tab}
              account={account}
              accounts={accounts}
              loading={loading}
              scanning={scanning}
              syncing={syncing}
              onScan={handleScan}
              onSync={handleSync}
            />
          }
        >
          <ConfidenceChips />
          <Datagrid
            rowClick="expand"
            expand={<Provenance />}
            bulkActionButtons={<BulkApproveButton />}
            empty={<NoMatches />}
          >
            <FunctionField<ContactSuggestion>
              label="Person"
              render={(record) => (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fullName(record)}
                  </Typography>
                  {record.email ? (
                    <Typography variant="body2" color="text.secondary">
                      {record.email}
                    </Typography>
                  ) : null}
                  {record.title || record.companyName ? (
                    <Typography variant="body2" color="text.secondary">
                      {[record.title, record.companyName].filter(Boolean).join(', ')}
                    </Typography>
                  ) : null}
                </Box>
              )}
            />
            <FunctionField<ContactSuggestion>
              label="Confidence"
              render={(record) => (
                <Chip
                  size="small"
                  label={record.confidence ?? 'low'}
                  color={confidenceColor(record.confidence)}
                />
              )}
            />
            {tab === 'INBOX_SCAN' ? (
              <FunctionField<ContactSuggestion>
                label="Referred by"
                render={(record) =>
                  record.referredByContact ? (
                    <MuiLink
                      component={RouterLink}
                      to={`/contacts/${record.referredByContact.id}/show`}
                      onClick={stopRowClick}
                      underline="hover"
                    >
                      {fullName(record.referredByContact)}
                    </MuiLink>
                  ) : (
                    <ReferrerPicker record={record} />
                  )
                }
              />
            ) : null}
            <FunctionField<ContactSuggestion>
              label="Reasoning"
              render={(record) => (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: 360,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {record.reasoning ?? ''}
                </Typography>
              )}
            />
            <FunctionField<ContactSuggestion>
              label="Source"
              render={(record) =>
                record.source === 'ADDRESS_BOOK' ? (
                  <Box>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={
                        /email history/i.test(record.sourceSubject ?? '') ? (
                          <MailOutlineIcon />
                        ) : (
                          <ContactsOutlinedIcon />
                        )
                      }
                      label={record.sourceSubject ?? 'Address book'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {record.sourceFrom ?? ''}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      {record.sourceWebLink ? (
                        <MuiLink
                          href={record.sourceWebLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                          {record.sourceSubject ?? '(no subject)'}
                        </MuiLink>
                      ) : (
                        (record.sourceSubject ?? '(no subject)')
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {record.sourceFrom ? `From ${record.sourceFrom} · ` : ''}
                      {formatDateTime(record.sourceReceivedAt)}
                    </Typography>
                    {record.sourceSnippet ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {record.sourceSnippet}
                      </Typography>
                    ) : null}
                  </Box>
                )
              }
            />
            <FunctionField label="" render={() => <RowActions />} />
          </Datagrid>
        </List>
      </RecordPage>
    </Container>
  );
};

export default ContactSuggestionList;
